import { ReactNode, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
  CircularProgress,
  IconButton,
  Popover,
  Switch,
} from "@mui/material";
import {
  ArrowBack,
  Cancel,
  CheckCircle,
  Circle,
  MoreHoriz,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getPlanningStatus, useCheckUserRole } from "@utils";
import {
  useWebApiV1UpdateOrderMutation,
  useWebApiV1OrderQuery,
  OrderOut,
  useWebApiV1GetUserFeatureSwitchesQuery,
  WebApiV1UpdateOrderApiArg,
} from "@providers/hop-ord-server/api";
import {
  Feature,
  planningStatusColourMapping,
  Role,
  StructureStatus,
} from "@enums";
import PageHeader from "./PageHeader";
import { PAGE_HEADER_HEIGHT } from "@constants";
import { AssignedToSelect, useAssignedToSelect } from "@components";

interface PageLeftHandSidebarProps {
  updateOrder: (order: WebApiV1UpdateOrderApiArg) => void;
  orderLoading: boolean;
  orderData: OrderOut | undefined;
  currentPageIndex: number;
  orderId: number;
}

interface Props {
  orderId: number;
  currentPageIndex: number;
  children: ReactNode;
}

const PageLeftHandSidebar = ({
  currentPageIndex,
  orderLoading,
  orderData,
  updateOrder,
}: PageLeftHandSidebarProps) => {
  const ctExists = orderData?.ctStatus === StructureStatus.COMPLETE;
  const rtStructExists = orderData?.rtstructStatus === StructureStatus.COMPLETE;
  const isTestCase = orderData?.isTest;
  const orderId = orderData?.id;

  const navigate = useNavigate();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const theme = useTheme();
  const isRo = useCheckUserRole(Role.RO);

  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();

  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  const navigationItems = showPlanReview
    ? ["Submit Careplan", "Plan Generation", "Plan Review", "Handover"]
    : ["Submit Careplan", "Plan Generation", "Handover"];

  const planningStatus = getPlanningStatus(
    orderData?.status,
    ctExists,
    rtStructExists,
    orderData?.handoverDatetime !== null,
  );
  const planningStatusColour = useMemo(() => {
    if (!planningStatus) {
      return "primary.main";
    }
    return planningStatusColourMapping[planningStatus];
  }, [planningStatus]);

  const {
    isEditing: isEditingAssignee,
    assignedTo,
    isUnassigned,
    options: assigneeOptions,
    handleOpen: handleOpenAssignee,
    handleClose: handleCloseAssignee,
    handleChange: handleChangeAssignee,
  } = useAssignedToSelect({
    orderId: orderId ?? 0,
    assignedDosimetrist: orderData?.assignedDosimetrist,
    assignablePlanners: orderData?.assignablePlanners,
  });

  return (
    <Stack
      sx={{
        width: "240px",
        minWidth: "240px",
        height: 1,
        bgcolor: "customBackground.main",
        borderRight: 1,
        borderColor: "divider",
      }}
    >
      <Stack
        direction="row"
        onClick={() => navigate("/commandcenter")}
        data-testid="back-to-command-center-button"
        sx={{
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          padding: 1,
          borderBottom: 1,
          borderColor: "divider",
          height: PAGE_HEADER_HEIGHT,
        }}
      >
        <IconButton>
          <ArrowBack />
        </IconButton>
        <Stack>
          <Typography variant="body1">Back</Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
            }}
          >
            Command Center
          </Typography>
        </Stack>
      </Stack>
      <Stack
        sx={{
          paddingX: 2,
          paddingY: 1,
          gap: 1,
        }}
      >
        {planningStatus && (
          <Stack>
            <Typography variant="body1">Planning Status</Typography>
            <Stack
              direction="row"
              sx={{
                gap: 1,
                alignItems: "center",
                justifyContent: "flex-start",
                paddingY: 0.5,
              }}
            >
              <Circle sx={{ color: planningStatusColour, fontSize: 20 }} />
              <Typography
                variant="body2"
                data-testid="left-sidebar-planning-status"
                sx={{
                  color: "text.primary",
                }}
              >
                {planningStatus}
              </Typography>
            </Stack>
          </Stack>
        )}
        <Stack
          sx={{
            gap: 0.5,
          }}
        >
          <Typography variant="body1">Assigned To</Typography>
          <AssignedToSelect
            isEditing={isEditingAssignee}
            assignedTo={assignedTo}
            isUnassigned={isUnassigned}
            options={assigneeOptions}
            onOpen={handleOpenAssignee}
            onClose={handleCloseAssignee}
            onChange={handleChangeAssignee}
            readOnly={isRo}
            buttonSx={{
              paddingY: 0.5,
              paddingRight: 1,
              marginLeft: -0.5,
              paddingLeft: 0.5,
            }}
            autocompleteSx={{
              width: "100%",
            }}
          />
        </Stack>
        <Stack
          sx={{
            gap: 0.5,
            paddingTop: 1,
          }}
        >
          <Typography variant="body1">RT Structure Set</Typography>
          <Chip
            color={
              orderLoading ? "secondary" : rtStructExists ? "success" : "error"
            }
            sx={{ width: "fit-content" }}
            icon={
              orderLoading ? (
                <CircularProgress size={18} />
              ) : rtStructExists ? (
                <CheckCircle />
              ) : (
                <Cancel />
              )
            }
            variant="outlined"
            label={rtStructExists ? "RTStruct Found" : "Not Found"}
          />
        </Stack>
      </Stack>
      <Divider sx={{ marginY: 1 }} />
      <Stack
        direction="row"
        sx={{
          paddingX: 2,
          gap: 3,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {isTestCase ? (
          <Chip
            label="Test Case"
            color="info"
            variant="outlined"
            sx={{ width: "fit-content" }}
          />
        ) : (
          <Chip
            label="Clinical Case"
            color="success"
            variant="outlined"
            sx={{ width: "fit-content" }}
          />
        )}
        <IconButton
          ref={anchorRef}
          onClick={() => setShowMoreOptions((prevOpen) => !prevOpen)}
          data-testid="pop-edit-button"
        >
          <MoreHoriz />
        </IconButton>
      </Stack>
      <Divider sx={{ marginY: 1 }} />
      <Stack>
        <List sx={{ width: 1 }} disablePadding>
          {navigationItems.map((item, idx) => (
            <ListItem
              key={idx}
              data-testid={`site-group-${idx}`}
              sx={{
                ...(idx == currentPageIndex
                  ? {
                      backgroundColor: "secondary.light",
                      borderLeft: `4px solid ${theme.palette.primary.main}`,
                    }
                  : {
                      borderLeft: `4px solid transparent`,
                    }),
              }}
            >
              <Stack
                direction="row"
                sx={{
                  gap: 3,
                  alignItems: "center",
                  justifyContent: "flex-start",
                  paddingLeft: 1,
                }}
              >
                {idx < currentPageIndex ? (
                  <Badge
                    badgeContent={
                      <CheckCircle color="primary" sx={{ padding: 0 }} />
                    }
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        backgroundColor: "transparent",
                        color: "primary.main",
                      },
                    }}
                  ></Badge>
                ) : (
                  <Badge badgeContent={idx + 1} color="primary"></Badge>
                )}
                <Typography variant="body1">{item}</Typography>
              </Stack>
            </ListItem>
          ))}
        </List>
      </Stack>
      <Popover
        id={"more-options-popover"}
        open={showMoreOptions}
        anchorEl={anchorRef.current}
        onClose={() => setShowMoreOptions(false)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Typography sx={{ p: 2 }}>
          Test case
          <Switch
            checked={isTestCase}
            onChange={() => {
              updateOrder({
                orderId: Number(orderId),
                orderIn: {
                  isTest: !isTestCase,
                },
              });
            }}
          />
        </Typography>
      </Popover>
    </Stack>
  );
};

const WorkflowPageWrapper = ({
  children,
  orderId,
  currentPageIndex,
}: Props) => {
  const [updateOrder] = useWebApiV1UpdateOrderMutation();
  const { data: order, isLoading } = useWebApiV1OrderQuery({ orderId });

  return (
    <Stack
      direction="row"
      sx={{
        height: 1,
        width: 1,
      }}
    >
      <PageLeftHandSidebar
        updateOrder={updateOrder}
        orderLoading={isLoading}
        orderData={order}
        currentPageIndex={currentPageIndex}
        orderId={orderId}
      />
      <Stack
        sx={{
          flexGrow: 1,
          minWidth: 0,
        }}
      >
        <PageHeader orderData={order} />
        <Stack
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          {children}
        </Stack>
        <Box id="bottom-action-bar" />
      </Stack>
    </Stack>
  );
};

export default WorkflowPageWrapper;
